import { useState, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import { MonitorPlay, IdCardLanyard, Copy, Check } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/client/components/ui/tooltip';
import { useClientSwapInfo } from '@/client/components/swap-client-dialog';
import { useCCSwapStore } from '@/client/store/ccSwapStore';
import { PLAYER_BASE_URL, CC_BASE_URL } from '@/client/config';

// ============================================================================
// Copyable Cell (reusable)
// ============================================================================

export function CopyableCell({
  value,
  className = '',
}: {
  value: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [value]);

  return (
    <button
      onClick={handleCopy}
      className={`font-mono text-xs hover:text-zinc-400 flex items-center gap-1 ${className}`}
    >
      {value}
      {copied ? (
        <Check className="h-3 w-3 text-green-500" />
      ) : (
        <Copy className="h-3 w-3 opacity-50" />
      )}
    </button>
  );
}

/**
 * Shared table cell components for consistent linking and formatting across tables.
 * All cells include stopPropagation to prevent row click interference.
 */

// ============================================================================
// Client Cells
// ============================================================================

export function ClientNameCell({
  clientID,
  clientName,
}: {
  clientID: bigint | string | number;
  clientName: string;
}) {
  return (
    <Link
      to="/clients/$clientID"
      params={{ clientID: String(clientID) }}
      className="hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {clientName}
    </Link>
  );
}

export function ClientIDCell({
  clientID,
  copyable = true,
}: {
  clientID: bigint | string | number;
  copyable?: boolean;
}) {
  const id = String(clientID);
  if (!copyable) return <span className="font-mono text-xs">{id}</span>;

  return <CopyableCell value={id} />;
}

// ============================================================================
// Event Cells
// ============================================================================

/**
 * Renders an event ID (display only, no external links).
 * Handles both "clientID_eventID" composite format and plain eventID.
 */
export function EventIDCell({
  eventID,
}: {
  eventID: bigint | string | number | null;
  /** @deprecated No longer used — kept for API compat during migration */
  clientID?: bigint | string | number | null;
  /** @deprecated */
  clientName?: string;
  /** @deprecated */
  linkToPlayer?: boolean;
}) {
  if (!eventID) return <span className="text-xs text-muted-foreground">-</span>;

  const rawID = String(eventID);
  const displayID = rawID.includes('_') ? rawID.split('_')[1] : rawID;

  return <span className="font-mono text-xs">{displayID}</span>;
}

/**
 * Renders Player and CC icon buttons for an event.
 * Player opens in a new tab. CC triggers client swap dialog if needed.
 */
export function EventLinksCell({
  eventID,
  clientID,
  clientName,
}: {
  eventID: bigint | string | number | null;
  clientID: bigint | string | number | null;
  clientName?: string;
}) {
  const clientIDStr = clientID ? String(clientID) : '';
  const { needsSwap } = useClientSwapInfo(clientIDStr);
  const requestSwap = useCCSwapStore((s) => s.requestSwap);

  if (!eventID || !clientID) return <span className="text-xs text-muted-foreground">-</span>;

  const rawID = String(eventID);
  const displayID = rawID.includes('_') ? rawID.split('_')[1] : rawID;

  const playerUrl = `${PLAYER_BASE_URL}/?clientID=${clientIDStr}&eventID=${displayID}&debug=true`;
  const ccUrl = `${CC_BASE_URL}/events/slim?eventID=${displayID}`;

  const handleCCClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (needsSwap) {
      requestSwap(clientIDStr, clientName || `Client ${clientIDStr}`, ccUrl);
    } else {
      window.open(ccUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <TooltipProvider>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <a
              href={playerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md btn-player-icon"
              onClick={(e) => e.stopPropagation()}
            >
              <MonitorPlay className="h-4 w-4" />
            </a>
          </TooltipTrigger>
          <TooltipContent><p>Open in Player</p></TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="inline-flex items-center justify-center h-7 w-7 rounded-md btn-cc-icon"
              onClick={handleCCClick}
            >
              <IdCardLanyard className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{needsSwap ? 'Switch client & open in Control Center' : 'Open in Control Center'}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}

export function EventTitleCell({
  title,
  maxLength = 60,
}: {
  title: string | null;
  maxLength?: number;
}) {
  if (!title) return <span className="text-xs text-muted-foreground">-</span>;

  if (title.length <= maxLength) {
    return <span className="text-xs">{title}</span>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-xs cursor-default">{title.slice(0, maxLength)}...</span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-md">{title}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ============================================================================
// Encoder Cells
// ============================================================================

export function EncoderCell({
  encoderID,
  encoderName,
}: {
  encoderID: number | string;
  encoderName?: string;
}) {
  return (
    <Link
      to="/encoders/$encoderID"
      params={{ encoderID: String(encoderID) }}
      className="hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {encoderName ?? `Encoder ${encoderID}`}
    </Link>
  );
}

export function EncoderIDCell({ encoderID }: { encoderID: number | string }) {
  return (
    <Link
      to="/encoders/$encoderID"
      params={{ encoderID: String(encoderID) }}
      className="font-mono text-xs hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {String(encoderID)}
    </Link>
  );
}
